﻿import React, { useState, useEffect } from 'react';
import MolecularFormulaParser from '../Chemistry/MolecularFormulaParser';
import { StandardReference } from '../../models/Standards/standard-reference';
import { QnmrCalculatorData } from '../../models/Calculators/qnmr-calculator-data';
import { QnmrCalculatorStandard } from '../../models/Calculators/qnmr-calculator-standard';
import { QnmrMeasurement } from '../../models/Calculators/qnmr-measurement';
import { CrudFunctions } from '../../utilities/crud-functions';
import GlobalDialog from '../Modal/GlobalDialog';
import { ModalMetadata } from '../../models/modal-metadata';
import { AssetType, BenchSheetTechnologyType, FieldUpdateType, ImageFormatType, ImageType, ModalMessageType, NoteType } from '../../models/enums';
import { SymbolEditorProps } from '../../models/symbol-editor-props';
import TextSymbolEditor from '../TextSymbolEditor';
import SecondaryBenchSheetInstrument from '../SecondaryBenchSheetInstrument';

import { TextSymbolEditorData } from '../../models/text-symbol-editor-data';
import NoteViewer from '../NoteViewer';
import DataTemplates from '../../templates/data-templates';
import { Asset } from '../../models/asset';
import { Formula } from '../../models/Calculators/formula';
import { PWDates } from '../../utilities/pw-dates';
import { PWMath } from '../../utilities/pw-math';
import { Decimal4Format, WholeDigitFormat } from '../../templates/pw-formatting';
import { FieldUpdate } from '../../models/field-update';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Summary,
    Item as ToolBarItem,
    TotalItem,
    Editing,
    Button as ColumnButton
} from 'devextreme-react/data-grid';

import {
    Chart,
    Series,
    ArgumentAxis,
    ValueAxis,
    Export,
    Legend,
    Margin,
    Title,
    Label,
    Font,
    Tooltip,
    Grid,
} from 'devextreme-react/chart';

import { Item } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import TextBox, { Button as TextBoxButton } from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import NumberBox from 'devextreme-react/number-box';
import RadioGroup from 'devextreme-react/radio-group';
import HtmlEditor from 'devextreme-react/html-editor';

const MOLECULAR_FORMULA_STRING = "molecularFormulaString";

let key = 1;

//temp
const grubbsTestCriticalValues = [
    { n: 3, a005: 1.153, a001: 1.155 },
    { n: 4, a005: 1.463, a001: 1.492 },
    { n: 5, a005: 1.672, a001: 1.749 },
    { n: 6, a005: 1.822, a001: 1.944 },
    { n: 7, a005: 1.938, a001: 2.097 },
    { n: 8, a005: 2.032, a001: 2.221 },
    { n: 9, a005: 2.110, a001: 2.323 },
    { n: 10, a005: 2.176, a001: 2.410 },
    { n: 11, a005: 2.234, a001: 2.485 },
    { n: 12, a005: 2.285, a001: 2.550 }
]

//temp
const physicalStateTypes = [
    { id: 1, name: 'Solid' },
    { id: 2, name: 'Liquid' }
];

//temp
const referenceMaterialTypes = [
    { id: 1, name: 'CRM' },
    { id: 2, name: 'RM' }
];

//temp
const measurementTypes = [
    { id: 1, name: 'Series' },
    { id: 2, name: 'Stability Test' }
];

const balanceTypes = [
    { id: 1, name: 'XP56' },
    { id: 2, name: 'XPR10' }
];

export default function QnmrCalculatorForm() {
    const [calculatorData, setCalculatorData] =
        useState<QnmrCalculatorData>();


    // TEMP --
    const [refChemicalFormula, setRefChemicalFormula] =
        useState<string>();

    const [sampleChemicalFormula, setSampleChemicalFormula] =
        useState<string>();

    const [calculationId, setCalculationId] =
        useState<string>();
    // --

    //temp
    const [benchSheetId, setBenchSheetId] =
        useState<string>();

    const [balances, setBalances] =
        useState<Asset[]>();

    const [selectedBalance, setSelectedBalance] =
        useState<Asset>();

    const [qnmrMeasurements, setQnmrMeasurements] =
        useState<QnmrMeasurement[]>();

    const [selectedQnmrMeasurement, setSelectedQnmrMeasurement] =
        useState<QnmrMeasurement>();

    const [standardsSearchDialogVisible, setStandardsSearchDialogVisible] =
        useState<boolean>();

    const [refMolecularFormulaParserDialogVisible, setRefMolecularFormulaParserDialogVisible] =
        useState<boolean>();

    const [sampleMolecularFormulaParserDialogVisible, setSampleMolecularFormulaParserDialogVisible] =
        useState<boolean>();

    const [standardSearchAndParserDialogVisible, setStandardSearchAndParserDialogVisible] =
        useState<boolean>();

    const [symbolEditorProps, setSymbolEditorProps] =
        useState<SymbolEditorProps>();

    const [revalidationDateCss, setRevalidationDateCss] =
        useState<string>("date-good");

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [showMore, setShowMore] =
        useState<boolean>();

    const [standardEdit, setStandardEdit] =
        useState<string>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    //temp
    const [balanceId, setBalanceId] =
        useState<number>();

    useEffect(() => {
        initializeUI();
        getAllData();
    }, []);

    useEffect(() => {
        setRevalidationDateCss(getValidationDateColor());
    }, [selectedBalance]);

    const parts = window.location.href.split('/');
    const calculatorId = parts[parts.length - 1];

    const refChemicalFormulaEditorButton = {
        text: 'Ω',
        stylingMode: 'text',
        width: 32,
        hint: "Insert Symbol",
        elementAttr: {
            id: 'chemicalFormula'
        },
        onClick: (e: any) => {
            refChemicalFormulaTextSymbolButton(e);
        }
    }

    const sampleChemicalFormulaEditorButton = {
        text: 'Ω',
        stylingMode: 'text',
        width: 32,
        hint: "Insert Symbol",
        elementAttr: {
            id: 'chemicalFormula'
        },
        onClick: (e: any) => {
            sampleChemicalFormulaTextSymbolButton(e);
        }
    }

    key++;
    //const gridWidth = 'calc(90vw - 13em)';

    const calculator = calculatorData && calculatorData.calculator ?
        calculatorData.calculator : undefined;

    const refStandard = calculatorData && calculatorData.refStandard ?
        calculatorData.refStandard : undefined;

    const sampleStandard = calculatorData && calculatorData.sampleStandard ?
        calculatorData.sampleStandard : undefined;

    const measurements = calculatorData && calculatorData.measurements ?
        calculatorData.measurements : undefined;

    const uncEvalmeasurements = calculatorData && calculatorData.uncertaintyEvaluation &&
        calculatorData.uncertaintyEvaluation.measurements ?
        calculatorData.uncertaintyEvaluation.measurements : undefined;

    const anovaStats = calculatorData && calculatorData.anovaStats ?
        calculatorData.anovaStats : undefined;

    return (
        <div className="pw-form-container">
            <div className="pw-content">
                <div id="pb-title-pane">
                    <div className="lq-title-bar">
                        <div className="lq-main-title">qNMR Calculator</div>
                        <div className="title-bar-spacer" />
                        <div className="save-toolbar">

                            {/*
                        <div>
                            <NavLink to={`/documentation/${DocumentationConstants.ProductPlanningForm}`}
                                className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                                <Button className="title-bar-button pw-orange"
                                    text="" icon="help" type="normal"
                                    hint="help and documentation" />
                            </NavLink>
                        </div>
                        */}

                            <div>
                                <Button className="title-bar-button"
                                    text="" icon="arrowleft" type="normal"
                                    hint="go back"
                                    onClick={goBack} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pw-comp-row">

                    {/* the left pane */}
                    <div className="qnmr-pane pw-margin-right-1">
                        <div className="qnmr-standard-box pw-bg-rb-100" >
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Reference</div>

                            <div className="pw-comp-row">
                                <div className="pw-comp-15">
                                    <div className="pw-form-unit-label">Product #</div>
                                    <TextBox id="refProductNumber"
                                        className="textbox"
                                        readOnly={true}
                                        value={sampleStandard && sampleStandard.productName ?
                                            sampleStandard.productName : undefined} />
                                </div>

                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Lot #</div>
                                    <TextBox id="refLotNumber"
                                        className="textbox"
                                        readOnly={true}
                                        value={sampleStandard && sampleStandard.batchName ?
                                            sampleStandard.batchName : undefined} />
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-comp-10">
                                    <div className="pw-form-unit-label">Mass Fraction (%)</div>
                                    <NumberBox id="massFractionPercent"
                                        className="textbox"
                                        value={calculator && calculator.massFractionPercent ?
                                            calculator.massFractionPercent : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Exp. Uncertainty (%)</div>
                                    <NumberBox id="expUncertaintyPercent"
                                        className="textbox"
                                        value={calculator && calculator.expUncertaintyPercent ?
                                            calculator.expUncertaintyPercent : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Min. Ref Weight (mg)</div>
                                    <NumberBox id="minRefWeight"
                                        className="textbox"
                                        value={calculator && calculator.minRefWeight ?
                                            calculator.minRefWeight : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-comp-10">
                                    <div className="pw-form-unit-label">Coverage Factor (k)</div>
                                    <NumberBox id="coverageFactor"
                                        className="textbox"
                                        value={calculator && calculator.coverageFactor ?
                                            calculator.coverageFactor : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Pretreatment</div>
                                    <NumberBox id="pretreatment"
                                        className="textbox"
                                        value={calculator && calculator.pretreatment ?
                                            calculator.pretreatment : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-comp-10">
                                    <div className="pw-form-unit-label">Traceability</div>
                                    <div className="pw-readonly-label"></div>
                                </div>
                                <div className="pw-form-unit pw-comp-10">
                                    <div className="pw-form-unit-label">Expiry Date</div>
                                    <div className="pw-readonly-label"></div>
                                </div>
                            </div>

                        </div>

                        {/* Ref molecular data pane */}
                        <div className="qnmr-molecular-data-box pw-bg-rb-100">
                            <div className="pw-comp-row">
                                <div className="pw-comp-30">
                                    <div className="pw-form-unit-label">Molecular Formula</div>
                                    <TextBox id="molecularFormulaString" className="textbox"
                                        showClearButton={true}
                                        onValueChanged={onCalculatorRefStandardValueChanged}
                                        value={refStandard && refStandard.molecularFormulaString ?
                                            refStandard.molecularFormulaString : undefined} placeholder='NA'>
                                        <TextBoxButton name="chemicalFormulaSubscriptButton" location="after"
                                            options={refChemicalFormulaEditorButton as any} />
                                    </TextBox>
                                </div>
                                <div className="pw-margin-top-08">
                                    <Button className="title-bar-button"
                                        text="Parse Formula" icon="formula" type="normal"
                                        hint="parse formula"
                                        onClick={showRefMolecularFormulaParserDialog} />
                                </div>
                            </div>

                            {refStandard && refStandard.molecularFormula &&
                                <>
                                    <div className="pw-comp-row">
                                        <div className="pw-form-unit pw-comp-10">
                                            <div className="pw-form-unit-label">Molecular Mass</div>
                                            <div className="pw-readonly-label">{refStandard.molecularFormula.molecularMass ?
                                                refStandard.molecularFormula.molecularMass : undefined}</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Uncertainty of Molecular Mass</div>
                                            <div className="pw-readonly-label">{refStandard.molecularFormula.uncertaintyOfMolecularMass ?
                                                refStandard.molecularFormula.uncertaintyOfMolecularMass : undefined}</div>
                                        </div>
                                    </div>

                                    <div className="pw-comp-row">
                                        <DataGrid height="12rem" width="100%" dataSource={refStandard.molecularFormula.elements ?
                                            refStandard.molecularFormula.elements : []}
                                            wordWrapEnabled={true}
                                            showBorders={true}>

                                            <Column dataField='atomicNumber' caption="Atomic #" dataType="number" width='12%' alignment="center" />
                                            <Column dataField='symbol' dataType="string" width='12%' alignment="center" />
                                            <Column dataField='name' dataType="string" width='25%' />
                                            <Column dataField='count' dataType="number" width='15%' />
                                            <Column dataField='iupacAtomicMass' caption="IUPAC Atomic Mass" dataType="number" width='18%' />
                                            <Column dataField='iupacAtomicMassUncertainty' caption="IUPAC Atomic Mass Unc" dataType="number" width='18%' />
                                            <Column />

                                        </DataGrid>
                                    </div>

                                    <div className="pw-comp-row">
                                        <div className="pw-comp-10">
                                            <div className="pw-form-unit-label">Number of Nuclei</div>
                                            <NumberBox id="numberOfNuclei" className="textbox"
                                                value={refStandard.numberOfNuclei ?
                                                    refStandard.numberOfNuclei : undefined}
                                                onValueChanged={onCalculatorRefStandardValueChanged} />
                                        </div>

                                        <div className="pw-form-unit pw-comp-10">
                                            <div className="pw-form-unit-label">Density</div>
                                            <NumberBox id="density" className="textbox"
                                                value={refStandard.density ?
                                                    refStandard.density : undefined}
                                                onValueChanged={onCalculatorRefStandardValueChanged} />
                                        </div>

                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Std Deviation of Density</div>
                                            <NumberBox id="density" className="textbox"
                                                value={refStandard.densityStandardDeviation ?
                                                    refStandard.densityStandardDeviation : undefined}
                                                onValueChanged={onCalculatorRefStandardValueChanged} />
                                        </div>
                                    </div>

                                    <div className="pw-comp-row">
                                        <div className="pw-comp-10">
                                            <div className="pw-form-unit-label">Air Buoyancy Factor</div>
                                            <div className="pw-readonly-label">{refStandard.airBuoyancyFactor ?
                                                refStandard.airBuoyancyFactor : 'NA'}</div>
                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                    </div>

                    {/* the right pane */}
                    <div className="qnmr-pane">
                        <div className="qnmr-standard-box pw-bg-rg-100" >
                            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Sample</div>

                            <div className="pw-comp-row">
                                <div className="pw-comp-15">
                                    <div className="pw-form-unit-label">Product #</div>
                                    <TextBox id="productName" className="textbox"
                                        value={calculatorData && calculatorData.calculator && calculatorData.calculator.productName ?
                                            calculatorData.calculator.productName : undefined}
                                        onValueChanged={onCalculatorSampleStandardValueChanged} />
                                </div>

                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Lot #</div>
                                    <TextBox id="lotName" className="textbox"
                                        value={calculatorData && calculatorData.calculator && calculatorData.calculator.lotName ?
                                            calculatorData.calculator.lotName : undefined}
                                        onValueChanged={onCalculatorSampleStandardValueChanged} />
                                </div>
                            </div>

                            <div>
                                <div>
                                    <div className="pw-form-unit-label">Process Request</div>
                                    <TextBox id="processRequest"
                                        className="textbox"
                                        value={calculator && calculator.processRequest ?
                                            calculator.processRequest : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                            </div>

                            <div className="pw-comp-row pw-margin-bottom-1">
                                <div className="pw-comp-12">
                                    <div className="pw-form-unit-label">Physical State</div>
                                    <RadioGroup id="physicalStateType" items={physicalStateTypes}
                                        displayExpr='name' valueExpr='id' layout="horizontal"
                                        value={calculator && calculator.physicalStateType ?
                                            calculator.physicalStateType : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                                <div className="pw-form-unit pw-comp-12">
                                    <div className="pw-form-unit-label">Reference Material</div>
                                    <RadioGroup id="referenceMaterialType" items={referenceMaterialTypes}
                                        displayExpr='name' valueExpr='id' layout="horizontal"
                                        value={calculator && calculator.referenceMaterialType ?
                                            calculator.referenceMaterialType : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Measurement</div>
                                    <RadioGroup id="measurementType" items={measurementTypes}
                                        displayExpr='name' valueExpr='id' layout="horizontal"
                                        value={calculator && calculator.measurementType ?
                                            calculator.measurementType : undefined}
                                        onValueChanged={onCalculatorValueChanged} />
                                </div>
                            </div>
                            <div>
                                <div className="pw-form-unit-label">NMR Spectra, file #</div>
                                <TextBox id="nmrSpectraFile"
                                    className="textbox"
                                    value={calculator && calculator.nmrSpectraFile ?
                                        calculator.nmrSpectraFile : undefined}
                                    onValueChanged={onCalculatorValueChanged} />
                            </div>
                            <div>
                                <div className="pw-form-unit-label">Measurement date/Visum</div>
                                <TextBox id="measurementDateVisum"
                                    className="textbox"
                                    value={calculator && calculator.measurementDateVisum ?
                                        calculator.measurementDateVisum : undefined}
                                    onValueChanged={onCalculatorValueChanged} />
                            </div>
                        </div>

                        <div className="qnmr-molecular-data-box pw-bg-rg-100">
                            <div className="pw-comp-row">
                                <div className="pw-comp-30">
                                    <div className="pw-form-unit-label">Molecular Formula</div>
                                    <TextBox id="molecularFormulaString" className="textbox"
                                        showClearButton={true}
                                        onValueChanged={onCalculatorSampleStandardValueChanged}
                                        value={sampleStandard && sampleStandard.molecularFormulaString ?
                                            sampleStandard.molecularFormulaString : undefined} placeholder='NA'>
                                        <TextBoxButton name="sampleChemicalFormulaSubscriptButton" location="after"
                                            options={sampleChemicalFormulaEditorButton as any} />
                                    </TextBox>
                                </div>
                                <div className="pw-margin-top-08">
                                    <Button className="title-bar-button"
                                        text="Parse Formula" icon="formula" type="normal"
                                        hint="parse formula"
                                        onClick={showSampleMolecularFormulaParserDialog} />
                                </div>
                            </div>

                            {sampleStandard && sampleStandard.molecularFormula &&
                                <>
                                    <div className="pw-comp-row">
                                        <div className="pw-form-unit pw-comp-10">
                                            <div className="pw-form-unit-label">Molecular Mass</div>
                                            <div className="pw-readonly-label">{sampleStandard.molecularFormula.molecularMass ?
                                                sampleStandard.molecularFormula.molecularMass : undefined}</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Uncertainty of Molecular Mass</div>
                                            <div className="pw-readonly-label">{sampleStandard.molecularFormula.uncertaintyOfMolecularMass ?
                                                sampleStandard.molecularFormula.uncertaintyOfMolecularMass : undefined}</div>
                                        </div>
                                    </div>

                                    <div>
                                        <DataGrid height="12rem" dataSource={sampleStandard.molecularFormula.elements ?
                                            sampleStandard.molecularFormula.elements : []}
                                            wordWrapEnabled={true}
                                            showBorders={true}>

                                            <Column dataField='atomicNumber' caption="Atomic #" dataType="number" width='12%' alignment="center" />
                                            <Column dataField='symbol' dataType="string" width='12%' alignment="center" />
                                            <Column dataField='name' dataType="string" width='25%' />
                                            <Column dataField='count' dataType="number" width='15%' />
                                            <Column dataField='iupacAtomicMass' caption="IUPAC Atomic Mass" dataType="number" width='18%' />
                                            <Column dataField='iupacAtomicMassUncertainty' caption="IUPAC Atomic Mass Unc" dataType="number" width='18%' />
                                            <Column />

                                        </DataGrid>
                                    </div>

                                    <div className="pw-comp-row">
                                        <div className="pw-comp-10">
                                            <div className="pw-form-unit-label">Number of Nuclei</div>
                                            <NumberBox id="numberOfNuclei" className="textbox"
                                                value={sampleStandard.numberOfNuclei ?
                                                    sampleStandard.numberOfNuclei : undefined}
                                                onValueChanged={onCalculatorSampleStandardValueChanged} />
                                        </div>

                                        <div className="pw-form-unit pw-comp-10">
                                            <div className="pw-form-unit-label">Density</div>
                                            <NumberBox id="density" className="textbox"
                                                value={sampleStandard.density ?
                                                    sampleStandard.density : undefined}
                                                onValueChanged={onCalculatorSampleStandardValueChanged} />
                                        </div>

                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Std Deviation of Density</div>
                                            <NumberBox id="density" className="textbox"
                                                value={sampleStandard.densityStandardDeviation ?
                                                    sampleStandard.densityStandardDeviation : undefined}
                                                onValueChanged={onCalculatorSampleStandardValueChanged} />
                                        </div>
                                    </div>

                                    <div className="pw-comp-row">
                                        <div className="pw-comp-10">
                                            <div className="pw-form-unit-label">Air Buoyancy Factor</div>
                                            <div className="pw-readonly-label">{sampleStandard.airBuoyancyFactor ?
                                                sampleStandard.airBuoyancyFactor : 'NA'}</div>
                                        </div>
                                    </div>
                                </>
                            }
                        </div>

                    </div>
                </div>
            </div>

            <div id="tab-buttons-panel" className="tab-buttons-container">
                <div id="summaryPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">hdr_weak</span> Summary</div>
                <div id="basicPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">hdr_weak</span> Basic</div>

                {/*
                <div id="balancePanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">steppers</span> Balance</div>
                */}
                <div id="measurementPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">timer</span> Measurement</div>
                <div id="evaluationPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">health_and_safety</span> Evaluation</div>
                <div id="lcMsIsotopicPurityPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">health_and_safety</span> LC-MS Isotopic Purity</div>
                <div id="anovaPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">build</span> ANOVA</div>
                <div id="outlierTestPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">format_list_bulleted</span> Outlier Test</div>
                <div id="massUncertaintyPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">widgets</span> Mass Uncertainty</div>
                <div id="chemicalReferencesPanelButton" className="tab-button" onClick={onPanelButtonClick}><span className="material-symbols-outlined tab-icon pw-blue">fact_check</span> Chemical References</div>
            </div>

            <div id="summaryPanel">
                <div className="lq-sub-title pw-margin-left-05 pw-margin-top-1 pw-margin-bottom-05">qNMR: Certified Content with Expanded Measurement Uncertainty</div>

                <div className="qnmr-summary-bg pw-margin-top-1">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="qnmr-summary-text pw-margin-bottom-05">Analyte mass fraction in sample</div>
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="qnmr-summary-text pw-margin-bottom-05"> {calculatorData &&
                                calculatorData.measurementStats && calculatorData.summary &&
                                calculatorData.summary.analyteMassFractionInSample ?
                                calculatorData.summary.analyteMassFractionInSample : 0} %</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="qnmr-summary-text">Analyte mass fraction in sample </div>
                            <div className="pw-readonly-label">(corrected by usotopic purity)</div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="qnmr-summary-text pw-margin-bottom-05">{calculatorData &&
                                calculatorData.summary && calculatorData.summary.analyteMassFractionInSampleCorrectedByIsotopicPurity ?
                                calculatorData.summary.analyteMassFractionInSampleCorrectedByIsotopicPurity : 'NA'} %</div>
                        </div>

                    </div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="qnmr-summary-text pw-margin-bottom-05">Exp. measurement uncertainty</div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="qnmr-summary-text pw-margin-bottom-05">{calculatorData &&
                                calculatorData.summary && calculatorData.summary.expMeasurementUncertainty ?
                                calculatorData.summary.expMeasurementUncertainty : 'NA'} %</div>
                        </div>

                    </div>
                </div>

            </div>

            <div id="basicPanel">
                <div className="pw-form-section">
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Climate</div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Temperature (°C)</div>
                            <NumberBox id="temperature" className="textbox"
                                value={calculator && calculator.temperature ?
                                    calculator.temperature : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Air Pressure (mbar)</div>
                            <NumberBox id="airPressure" className="textbox"
                                value={calculator && calculator.airPressure ?
                                    calculator.airPressure : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Relative Air Humidity (% rh)</div>
                            <NumberBox id="relativeHumidity" className="textbox"
                                value={calculator && calculator.relativeHumidity ?
                                    calculator.relativeHumidity : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                    </div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Calculated Air Density</div>
                            <div className="pw-readonly-label">{calculator && calculator.calculatedAirDensity ?
                                calculator.calculatedAirDensity : '0.0000'}</div>
                        </div>
                    </div>

                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Balance</div>


                    <div className="pw-form-unit pw-comp-12">
                        <div className="pw-form-unit-label">Reference Material</div>
                        <RadioGroup items={balanceTypes}
                            displayExpr='name' valueExpr='id' layout="horizontal"
                            value={balanceId ? balanceId : 1}
                            onValueChanged={onBalanceValueChanged} />
                    </div>


                    <div>
                        <SecondaryBenchSheetInstrument isReadOnly={false}
                            benchSheetId={benchSheetId}
                            technology={BenchSheetTechnologyType.QNuclearMagneticResonance}
                            title="Balance" overrideInstrumentsUrl="assets/allbalances" />
                    </div>

                </div>
            </div>

            {/*
            <div id="balancePanel">
                <div className="pw-form-section">

                    <SecondaryBenchSheetInstrument isReadOnly={false}
                        benchSheetId={benchSheetId}
                        technology={BenchSheetTechnologyType.QNuclearMagneticResonance}
                        assetType={AssetType.Balance}
                        title="Balance" />




                    
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Balance</div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-40">
                            <div className="pw-form-unit-label">Balance</div>
                            <SelectBox
                                placeholder='balance'
                                displayExpr='assetNumber'
                                className="asset-selectbox"
                                height='2.1em'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={balances}
                                onValueChanged={onBalanceChanged}
                                valueExpr='assetId'
                                displayValue='assetNumber'
                                value={calculatorData && calculatorData.balanceId ? calculatorData.balanceId : undefined} />
                        </div>
                    </div>

                    <div className="pw-flex-wrapped-row-section">
                        <div id="instrument-left-pane">
                            <div className="pw-comp-row">
                                <div className="pw-comp-15">
                                    <div className="pw-form-unit-label">Asset Number</div>
                                    <div className="pw-readonly-label">{selectedBalance ? selectedBalance.assetNumber : 'NA'}</div>
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Asset Name</div>
                                    <div className="pw-readonly-label">{selectedBalance ? selectedBalance.assetName: 'NA'}</div>
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-form-unit-label">Revalidation Date</div>
                                    <div className={`pw-readonly-label ${revalidationDateCss}`}>
                                        {selectedBalance && selectedBalance.revalidationDate ? PWDates.getDateString(selectedBalance.revalidationDate) : 'NA'}
                                    </div>
                                </div>
                            </div>

                            {showMore &&
                                <div className="show-more-link" onClick={onShowMoreClick} >Show more...</div>
                            }
                            {!showMore &&
                                <>
                                    <div className="pw-comp-row">
                                        <div>
                                            <div className="pw-form-unit-label">Description</div>
                                            <div className="pw-readonly-label">{selectedBalance && selectedBalance.description ?
                                                selectedBalance.description : 'NA'}</div>
                                        </div>
                                    </div>

                                    <div className="pw-comp-row">
                                        <div className="pw-comp-15">
                                            <div className="pw-form-unit-label">Model Number</div>
                                            <div className="pw-readonly-label">{selectedBalance && selectedBalance.modelNumber ?
                                                selectedBalance.modelNumber : 'NA'}</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Serial Number</div>
                                            <div className="pw-readonly-label">{selectedBalance && selectedBalance.vendorSerialNumber ?
                                                selectedBalance.vendorSerialNumber : 'NA'}</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-form-unit-label">Location</div>
                                            <div className="pw-readonly-label">{selectedBalance && selectedBalance.location ?
                                                selectedBalance.location : 'NA'}</div>
                                        </div>
                                    </div>
                                    <div className="show-more-link" onClick={onShowMoreClick} >Show less...</div>
                                </>
                            }
                        </div>
                    </div>
                    

                </div>
            </div>
            */}

            <div id="measurementPanel">
                <div className="pw-margin-top-1">
                    <DataGrid height="25rem"
                        width="100%"
                        keyExpr="qnmrMeasurementId"
                        dataSource={calculatorData && calculatorData.measurements ? calculatorData.measurements : []}
                        onSelectionChanged={onQnmrMeasurementDataGridSelectionChanged}
                        onRowValidating={onSelectedQnmrMeasurementsValidating}
                        showBorders={true}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode="nextColumn" >

                        <Selection mode="single" />

                        <Column type="buttons" width={"6rem"}>
                            <ColumnButton name="edit" />
                            <ColumnButton hint="audit information" icon="clock"
                                onClick={onQnmrMeasurementAuditButtonClicked} />
                            <ColumnButton hint="information" icon="info"
                                onClick={onQnmrMeasurementInformationButtonClicked} />
                        </Column>

                        <Column caption="Reference" alignment="center">
                            <Column dataField='refWeight' width='8rem'
                                caption="Weight (mg)" dataType="number" allowEditing={false} />
                            <Column dataField='refMass' width='8rem' caption="Mass (mg)"
                                dataType="number" allowEditing={false} />
                            <Column dataField='refIntegral' width='7rem'
                                caption="Intensity" dataType="number" />
                        </Column>

                        <Column dataField='sampleIdx' width='6rem' caption="Sample"
                            dataType="number" allowEditing={false} alignment="center" />

                        <Column caption="Sample" alignment="center">
                            <Column dataField='sampleWeight' width='8rem'
                                caption="Weight (mg)" dataType="number" allowEditing={false} />
                            <Column dataField='sampleMass' width='8rem'
                                caption="Mass (mg)" dataType="number" allowEditing={false} />
                            <Column dataField='sampleIntegral' width='8rem'
                                caption="Intensity" dataType="number" />
                        </Column>

                        <Column dataField='massFraction' width='8rem' caption="Mass Fraction (%)"
                            dataType="number" allowEditing={false} />
                        <Column dataField='outlier' width='8rem' caption="Outlier?" dataType="boolean" />
                        <Column dataField='massFractionIfOutlier' width='8rem'
                            caption="Mass Fraction (%) if outlier" dataType="number" allowEditing={false} />
                        <Column allowEditing={false} />

                        <Summary>
                            <TotalItem valueFormat="###0.###"
                                column="refWeight"
                                displayFormat="Mean: {0}"
                                summaryType="avg" />
                            <TotalItem valueFormat="###0.###"
                                column="sampleWeight"
                                displayFormat="Mean: {0}"
                                summaryType="avg" />
                            <TotalItem valueFormat="###0.###"
                                column="massFraction"
                                displayFormat="Mean: {0}"
                                summaryType="avg" />
                        </Summary>

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">qNMR Measurement</div>
                                </div>
                            </ToolBarItem>

                        </Toolbar>

                        <Editing mode="row"
                            useIcons={true}
                            allowAdding={false}
                            allowUpdating={true}
                            allowDeleting={false} />

                    </DataGrid>
                </div>

                {measurements &&
                    <div>
                        <Chart dataSource={measurements} width="30rem" >
                            <Series type="scatter" argumentField="sampleIdx" valueField="massFraction" color="#e37730" />
                            <Margin bottom={20} />
                            <ValueAxis>
                                <Title text="Mass Fraction (%)">
                                    <Font weight={800} />
                                </Title>
                            </ValueAxis>
                            <ArgumentAxis valueMarginsEnabled={false}
                                tickInterval={1} wholeRange={[0, 10]} visualRange={[0, 10]}
                                discreteAxisDivisionMode="crossLabels">
                                <Grid visible={true} />
                                <Title text="Sample no." >
                                    <Font weight={800} />
                                </Title>
                            </ArgumentAxis>
                            <Export enabled={true} />
                            <Title text="Mass Fraction" />
                            <Legend visible={false} />
                            <Tooltip enabled={true} />
                        </Chart>
                    </div>
                }
            </div>

            <div id="evaluationPanel">
                <div className="pw-margin-top-1">

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Integration</div>
                            <NumberBox id="integration" className="textbox"
                                value={calculator && calculator.integration ?
                                    calculator.integration : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                    </div>

                    <DataGrid height="22rem"
                        width="100%"
                        dataSource={calculatorData && calculatorData.uncertaintyEvaluation &&
                            calculatorData.uncertaintyEvaluation.measurements ?
                            calculatorData.uncertaintyEvaluation.measurements : []}
                        showBorders={true}
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        wordWrapEnabled={true}
                        allowColumnResizing={true}
                        columnResizingMode="nextColumn" >

                        <Selection mode="single" />

                        <Column dataField='position' width='8rem'
                            dataType="number" alignment="center" />
                        <Column dataField='label' width='12rem' caption=""
                            dataType="string" />
                        <Column dataField='u' width='7rem'
                            caption="u" dataType="number" />
                        <Column dataField='value' width='10rem' caption="Value"
                            dataType="number" />
                        <Column dataField='rsd' width='10rem'
                            caption="RSD" dataType="number" />
                        <Column dataField='rsd2' width='10rem'
                            caption="RSD²" dataType="number" />
                        <Column dataField='contribution' width='10rem' format="###0.0"
                            caption="Contribution (%)" dataType="number" />
                        <Column dataField='relativeContribution' width='10rem' format="###0.000"
                            caption="Relative Contribution (%)" dataType="number" />

                        <Column />

                        <Summary>
                            <TotalItem valueFormat="###0.###"
                                column="rsd2"
                                displayFormat="comb. MU (uc): {0}"
                                summaryType="sum" />
                        </Summary>

                        <Toolbar>
                            <ToolBarItem location='before'>
                                <div className="pw-datagrid-toolbar-template">
                                    <div className="pw-datagrid-toobar-title">qNMR: Evaluation of Measured Uncertainty</div>
                                </div>
                            </ToolBarItem>
                        </Toolbar>

                    </DataGrid>
                </div>

                {uncEvalmeasurements &&
                    <div>
                        <Chart dataSource={uncEvalmeasurements} width="30rem">
                            <Series type="bar" argumentField="position" valueField="contribution" color="#ccffaa">
                                <Label template={contributionBarChartValueAxisRender} />
                            </Series>
                            <ValueAxis>
                                <Title text="Contribution (%)">
                                    <Font weight={800} />
                                </Title>
                            </ValueAxis>
                            <Margin bottom={20} />
                            <ArgumentAxis valueMarginsEnabled={true}
                                tickInterval={1} minValueMargin={0.1}
                                discreteAxisDivisionMode="crossLabels">
                                <Grid visible={true} />
                            </ArgumentAxis>
                            <Export enabled={true} />
                            <Legend visible={false} />
                            <Title text="Contribution to the combined measurement" />
                            <Tooltip enabled={true} />
                        </Chart>
                    </div>
                }

            </div>

            <div id="lcMsIsotopicPurityPanel">
                <div className="pw-form-section">
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">LC-MS Isotopic Purity: Result and Evaluation of Measurement Uncertainty</div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Isotopic Purity U</div>
                            <NumberBox id="isotopicPurityU" className="textbox"
                                value={calculator && calculator.isotopicPurityU ?
                                    calculator.isotopicPurityU : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>

                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-form-unit-label">Isotopic Purity Value</div>
                            <NumberBox id="isotopicPurityValue" className="textbox"
                                value={calculator && calculator.isotopicPurityValue ?
                                    calculator.isotopicPurityValue : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                        </div>
                        <div className="pw-bold pw-comp-15">
                            <div className="pw-readonly-label">u</div>
                        </div>
                        <div className="pw-bold pw-comp-20">
                            <div className="pw-readonly-label">Relative contribution (%)</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Uncertainty from qNMR measurement (u<sub>char</sub>)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromQnmrMeasurement ?
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromQnmrMeasurement : 'NA'}</div>
                        </div>

                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Uncertainty from Inhomogeneity (u<sub>hom</sub>)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromInhomogeneity ?
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromInhomogeneity : 'NA'}</div>
                        </div>
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromInhomogeneityRelativeContribution ?
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromInhomogeneityRelativeContribution : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Uncertainty from Stability Tests (u<sub>stab</sub>)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <NumberBox id="stabilityTestsUncertainty" className="textbox" width="6rem"
                                value={calculator && calculator.stabilityTestsUncertainty ?
                                    calculator.stabilityTestsUncertainty : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromStabilityTestsRelativeContribution ?
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromStabilityTestsRelativeContribution : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Additional uncertainty contribution (u<sub>additional</sub>)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <NumberBox id="additionalUncertaintyContribution" className="textbox" width="6rem"
                                value={calculator && calculator.additionalUncertaintyContribution ?
                                    calculator.additionalUncertaintyContribution : undefined}
                                onValueChanged={onCalculatorValueChanged} />
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Uncertainty from LC-MS measurement (u<sub>isotop</sub>)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromLcMsMeasurement ?
                                calculatorData.lcMsIsotopicPurityEvaluation.uncertaintyFromLcMsMeasurement : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-bold">Combined Standard Uncertainty, u<sub>c</sub></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.combinedStandardUncertainty ?
                                calculatorData.lcMsIsotopicPurityEvaluation.combinedStandardUncertainty : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row pw-margin-top-1">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Number of measurements (n)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.numberOfMeasurements ?
                                calculatorData.lcMsIsotopicPurityEvaluation.numberOfMeasurements : 'NA'}</div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-30">
                            <div className="pw-readonly-label"><span>Coverage factor (k)</span></div>
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <div className="pw-readonly-label">{calculatorData && calculatorData.lcMsIsotopicPurityEvaluation &&
                                calculatorData.lcMsIsotopicPurityEvaluation.coverageFactor ?
                                calculatorData.lcMsIsotopicPurityEvaluation.coverageFactor : 'NA'}</div>
                        </div>
                    </div>

                </div>
            </div>

            <div id="anovaPanel">
                <div className="pw-form-section">
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">One-way ANOVA</div>

                    <div className="pw-margin-top-1">
                        <DataGrid height="10rem"
                            width="100%"
                            dataSource={calculatorData && calculatorData.oneWayAnova &&
                                calculatorData.oneWayAnova.measurements ?
                                calculatorData.oneWayAnova.measurements : []}
                            showBorders={true}
                            rowAlternationEnabled={true}
                            showRowLines={true}
                            wordWrapEnabled={true}
                            allowColumnResizing={true}
                            columnResizingMode="nextColumn" >

                            <Selection mode="none" />

                            <Column dataField='label' width='8rem'
                                caption="" dataType="string" />
                            <Column dataField='value1' width='8rem' caption="Value 1"
                                dataType="number" />
                            <Column dataField='value2' width='8rem' caption="Value 2"
                                dataType="number" />
                            <Column dataField='value3' width='8rem' caption="Value 3"
                                dataType="number" />
                            <Column dataField='value4' width='8rem' caption="Value 4"
                                dataType="number" />
                            <Column dataField='measurements' width='7rem'
                                dataType="number" />

                            <Column dataField='squareSum' width='8rem'
                                caption="SS" dataType="number" />

                            <Column dataField='mean' width='8rem'
                                dataType="number" allowEditing={false} />
                            <Column dataField='variance' width='8rem'
                                dataType="number" />

                            <Column dataField='variationOfGroups' width='8rem' caption="Variation of Groups"
                                dataType="number" allowEditing={false} />
                            <Column />

                            <Summary>
                                <TotalItem displayFormat="Total Mean: {0}" column="value1" summaryType="avg"
                                    valueFormat={Decimal4Format} alignment="right" />
                            </Summary>

                            <Summary>
                                <TotalItem displayFormat="Min. Meas: {0}" column="measurements" summaryType="min"
                                    valueFormat={WholeDigitFormat} alignment="right" />
                            </Summary>

                        </DataGrid>
                    </div>

                    {anovaStats &&
                        <div className="pw-margin-top-1">
                            <DataGrid height="10rem"
                                width="100%"
                                dataSource={anovaStats}
                                showBorders={true}
                                rowAlternationEnabled={true}
                                showRowLines={true}
                                wordWrapEnabled={true}
                                allowColumnResizing={true}
                                columnResizingMode="nextColumn" >

                                <Selection mode="none" />

                                <Column dataField='label' width='8rem'
                                    caption="" dataType="string" />
                                <Column dataField='sumOfSquares' width='8rem' caption="SS"
                                    dataType="number" />
                                <Column dataField='degreesOfFreedom' width='8rem' caption="df"
                                    dataType="number" />
                                <Column dataField='ms' width='8rem' caption="MS"
                                    dataType="number" />
                                <Column dataField='testValueF' width='8rem' caption="Test Value (F)"
                                    dataType="number" />
                                <Column dataField='pValue' width='7rem' caption="P-Value"
                                    dataType="number" />
                                <Column dataField='criticalFValue' width='8rem'
                                    caption="crit. F-Value" dataType="number" />
                                <Column />
                            </DataGrid>

                            <div className="pw-comp-row pw-margin-top-1">
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-readonly-label pw-bold"><span>u<sup>2</sup><sub>bb</sub>(min)</span></div>
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                        calculatorData.oneWayAnova.u2bbMin ?
                                        calculatorData.oneWayAnova.u2bbMin : 'NA'} %</div>
                                </div>
                            </div>

                            <div className="pw-comp-row">
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-readonly-label pw-bold"><span>u<sup>2</sup><sub>bb</sub>(max)</span></div>
                                </div>
                                <div className="pw-form-unit pw-comp-15">
                                    <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                        calculatorData.oneWayAnova.u2bbMax ?
                                        calculatorData.oneWayAnova.u2bbMax : 'NA'} %</div>
                                </div>
                            </div>

                            <div className="pw-comp-40" style={{ border: "1px solid black" }}>

                                <div style={{ backgroundColor: "orange", borderBottom: "1px solid black" }}>
                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">TOP, mean value</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.topMeanValue ?
                                                calculatorData.oneWayAnova.topMeanValue : 'NA'}</div>
                                        </div>
                                    </div>

                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">TOP, standard deviation</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.topStdDeviation ?
                                                calculatorData.oneWayAnova.topStdDeviation : 'NA'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ backgroundColor: "gray", borderBottom: "1px solid black" }}>
                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">MIDDLE, mean value</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.middleMeanValue ?
                                                calculatorData.oneWayAnova.middleMeanValue : 'NA'}</div>
                                        </div>
                                    </div>

                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">MIDDLE, standard deviation</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.middleStdDeviation ?
                                                calculatorData.oneWayAnova.middleStdDeviation : 'NA'}</div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ backgroundColor: "lightgreen" }}>
                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">BOTTOM, mean value</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.bottomMeanValue ?
                                                calculatorData.oneWayAnova.bottomMeanValue : 'NA'}</div>
                                        </div>
                                    </div>

                                    <div className="flex-col-stretch">
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">BOTTOM, standard deviation</div>
                                        </div>
                                        <div className="pw-form-unit pw-comp-15">
                                            <div className="pw-readonly-label pw-bold">{calculatorData && calculatorData.oneWayAnova &&
                                                calculatorData.oneWayAnova.bottomStdDeviation ?
                                                calculatorData.oneWayAnova.bottomStdDeviation : 'NA'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    }

                </div>
            </div>


            <div id="outlierTestPanel">

                <div className="pw-comp-row pw-margin-top-1">
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Outlier test (Grubb's test)</div>
                </div>

                <div className="pw-comp-row pw-margin-top-1">
                    <div className="pw-form-unit pw-comp-20">
                        <DataGrid height="20rem"
                            width="100%"
                            dataSource={calculatorData && calculatorData.grubbsTestData &&
                                calculatorData.grubbsTestData.measurements ?
                                calculatorData.grubbsTestData.measurements : []}
                            showBorders={true}
                            rowAlternationEnabled={true}
                            showRowLines={true}
                            wordWrapEnabled={true}>

                            <Selection mode="none" />

                            <Column dataField='measurementId' width='8rem'
                                caption="measurement" dataType="string" alignment="center" />
                            <Column dataField='value' width='8rem' caption="Value" alignment="center"
                                cellRender={formatGrubbsTestMeasurement} dataType="number" />

                        </DataGrid>
                    </div>

                    <div>
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">Mean value</div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.mean ?
                                    calculatorData.grubbsTestData.mean : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">Std Deviation</div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.stdDev ?
                                    calculatorData.grubbsTestData.stdDev : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">Minimum Value</div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.min ?
                                    calculatorData.grubbsTestData.min : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">Maximum Value</div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.max ?
                                    calculatorData.grubbsTestData.max : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">PG<sub>1</sub></div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.pg1 ?
                                    calculatorData.grubbsTestData.pg1 : 'NA'}</div>
                            </div>
                        </div>

                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">PG<sub>2</sub></div>
                            </div>
                            <div className="pw-form-unit pw-comp-15">
                                <div className="pw-readonly-label">{calculatorData && calculatorData.grubbsTestData &&
                                    calculatorData.grubbsTestData.pg2 ?
                                    calculatorData.grubbsTestData.pg2 : 'NA'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/*
                    <div className="pw-form-unit pw-comp-30">
                        <DataGrid height="20rem"
                            width="100%"
                            dataSource={grubbsTestCriticalValues ? grubbsTestCriticalValues : []}
                            showBorders={true}
                            rowAlternationEnabled={true}
                            showRowLines={true}
                            wordWrapEnabled={true}>

                            <Selection mode="none" />

                            <Column dataField='n' width='5rem' alignment="center"
                                caption="n" dataType="string" />

                            <Column caption="level of significance" alignment="center">
                                <Column dataField='a005' width='8rem' caption="a = 0.05"
                                    alignment="center" dataType="number" cellRender={criticalValuesForGrubbsTestRender} />
                                <Column dataField='a001' width='8rem' caption="a = 0.01"
                                    alignment="center" dataType="number" />
                            </Column>

                            <Toolbar>
                                <ToolBarItem location='before'>
                                    <div className="pw-datagrid-toolbar-template">
                                        <div className="pw-datagrid-toobar-title">Critical Values for Grubb's Test</div>
                                    </div>
                                </ToolBarItem>
                            </Toolbar>
                        </DataGrid>
                    </div>
                    */}

            </div>

            <div id="massUncertaintyPanel">
                <div className="pw-comp-row pw-margin-top-1">
                    <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Mass Uncertainty Report</div>
                </div>

                <HtmlEditor height="50rem" readOnly={true}
                    value={calculatorData && calculatorData.calculationReport ? calculatorData.calculationReport : "<div>NA</div>"} />
            </div>

            <div id="chemicalReferencesPanel">

            </div>

            <div style={{ height: "60rem" }}></div>

            {/* Notes cannot go in a panel because an "Overlay" component blocks the 
                click event over several of the tabs" */}
            {/*
            <div className="pw-comp-row-scroll-fixed-grid">
                <NoteViewer height='30rem' title="Calculation Notes"
                    noteType={NoteType.QnmrCalculation} parentId={calculatorId} />
            </div>
            */}

            {
                refMolecularFormulaParserDialogVisible &&
                <DialogPopup
                    visible={refMolecularFormulaParserDialogVisible}
                    onHiding={hideRefMolecularFormulaParserDialog}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="Molecular Formula Parser"
                    width="80vw"
                    height="80vh">

                    <MolecularFormulaParser key={`mfp${key}`}
                        formula={calculatorData.refStandard && calculatorData.refStandard.molecularFormula &&
                            calculatorData.refStandard.molecularFormula.name ?
                            calculatorData.refStandard.molecularFormula.name : undefined}
                        onApplySelection={onApplyRefFormattedMolecularFormula}
                        onCancel={hideRefMolecularFormulaParserDialog} />
                </DialogPopup>
            }

            {
                sampleMolecularFormulaParserDialogVisible &&
                <DialogPopup
                    visible={sampleMolecularFormulaParserDialogVisible}
                    onHiding={hideSampleMolecularFormulaParserDialog}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="Molecular Formula Parser"
                    width="80vw"
                    height="80vh">

                    <MolecularFormulaParser key={`mfp2${key}`}
                        formula={calculatorData.sampleStandard && calculatorData.sampleStandard.molecularFormula &&
                            calculatorData.sampleStandard.molecularFormula.name ?
                            calculatorData.sampleStandard.molecularFormula.name : undefined}
                        onApplySelection={onApplySampleFormattedMolecularFormula}
                        onCancel={hideSampleMolecularFormulaParserDialog} />
                </DialogPopup>
            }

            {
                symbolEditorProps && symbolEditorProps.editorVisible &&
                <DialogPopup
                    visible={symbolEditorProps.editorVisible}
                    onHiding={hideSymbolEditor}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showTitle={true}
                    title="Symbol Editor"
                    width={700}
                    height={450}>

                    <TextSymbolEditor key={`se${key}`}
                        textInput={symbolEditorProps.inputText}
                        propName={symbolEditorProps.propName}
                        onApply={textSymbolApply}
                        onCancel={textSymbolCancel} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div >
    );


    //temp

    function onBalanceValueChanged(e: any) {
        setBalanceId(e.value);
    }

    function onApplyRefFormattedMolecularFormula(formula: Formula) {
        console.log("onApplyRefFormattedMolecularFormula");
        console.log(formula);

    }

    function onApplySampleFormattedMolecularFormula(formula: Formula) {
        console.log("onApplySampleFormattedMolecularFormula");
        console.log(formula);

    }

    function onBenchSheetIdChanged(e: any) {
        setBenchSheetId(e.value);
    }

    function onBalanceChanged(e: any) {
        // setCalculatorData({ ...calculatorData, balanceId: e.value });
    }

    function onQnmrMeasurementDataGridSelectionChanged(e: any) {
        console.log("onQnmrMeasurementDataGridSelectionChanged");
        console.log(e);


    }

    async function onSelectedQnmrMeasurementsValidating(e: any) {
        console.log("onSelectedQnmrMeasurementsValidating");
        console.log(e);

        /*
        if (e.brokenRules && e.brokenRules.length > 0) {
                e.cancel = true;
            return;
        }
            */

        if (e.oldData) {
            let measurement = e.oldData as QnmrMeasurement;

            if (measurement) {
                let updatedMeasurement = { ...measurement, ...e.newData };

                await updateQnmrMeasurement(updatedMeasurement)
                    .catch((res) => {
                        //refreshData();
                        showMessage(res, ModalMessageType.Error, "Error Updating qNMR Measurement");
                    });
            }
        }


    }

    function onQnmrMeasurementAuditButtonClicked(e: any) {

    }

    function onQnmrMeasurementInformationButtonClicked(e: any) {

    }

    //not needed?
    function onQnmrUncertaintyEvaluationDataGridSelectionChanged(e: any) {

    }

    async function textSymbolApply(data: TextSymbolEditorData) {
        console.log("textSymbolApply")
        console.log(data);

        if (data) {
            let propName = data.propName;

            if (propName === MOLECULAR_FORMULA_STRING) {
                if (standardEdit === "ref") {
                    const editedRefStandard = { ...calculatorData.refStandard };

                    editedRefStandard.molecularFormulaString = data.text;

                    setCalculatorData({ ...calculatorData, refStandard: editedRefStandard });

                    let fieldUpdate: FieldUpdate = {
                        field: MOLECULAR_FORMULA_STRING,
                        value: data.text,
                        routeId: 0,
                        rowId: calculatorId
                    };

                    await updateQnmrCalculatorStandard(fieldUpdate)
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Updating qNMR Field");
                        });
                }
                else {
                    const editedSampleStandard = { ...calculatorData.sampleStandard };

                    editedSampleStandard.molecularFormulaString = data.text;

                    setCalculatorData({ ...calculatorData, sampleStandard: editedSampleStandard });

                    let fieldUpdate: FieldUpdate = {
                        field: MOLECULAR_FORMULA_STRING,
                        value: data.text,
                        routeId: 1,
                        rowId: calculatorId
                    };

                    console.log("ABOUT TO UPDATE REF");

                    await updateQnmrCalculatorStandard(fieldUpdate)
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Updating qNMR Field");
                        });
                }
            }

            hideSymbolEditor();
        }
    }

    function textSymbolCancel() {
        hideSymbolEditor();
    }

    async function onCalculatorValueChanged(e: any) {
        //console.log("onHeaderValueChanged");
        //console.log(e);

        if (!dataLoaded || !calculatorData)
            return;

        const caller = e.element.id;

        if (caller) {
            const cd = calculatorData.calculator as any;
            const currentValue = cd[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                let editedCalculator = { ...calculatorData.calculator };

                editedCalculator[caller] = e.value;

                let calculatedAirDensity = calculateClimateAirDensity();
                editedCalculator.calculatedAirDensity = calculatedAirDensity;

                setCalculatorData({ ...calculatorData, calculator: editedCalculator });

                let fieldUpdate: FieldUpdate = {
                    field: caller,
                    value: e.value,
                    rowId: calculatorId
                };

                console.log("ABOUT TO UPDATE CALC");

                await updateQnmrCalculatorField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating qNMR Field");
                    });
            }
        }
    }

    async function onCalculatorRefStandardValueChanged(e: any) {
        console.log("onCalculatorRefStandardValueChanged");
        console.log(e);

        if (!dataLoaded || !calculatorData)
            return;

        const caller = e.element.id;

        if (caller) {
            const cd = calculatorData.refStandard as any;
            const currentValue = cd[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                let editedRefStandard = { ...refStandard };

                editedRefStandard[caller] = e.value;

                setCalculatorData({ ...calculatorData, refStandard: editedRefStandard });

                let fieldUpdate: FieldUpdate = {
                    field: caller,
                    value: e.value,
                    routeId: 0,
                    rowId: calculatorId
                };

                console.log("ABOUT TO UPDATE REF");

                await updateQnmrCalculatorStandard(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating qNMR Field");
                    });
            }
        }
    }

    async function onCalculatorSampleStandardValueChanged(e: any) {
        if (!dataLoaded || !calculatorData)
            return;

        const caller = e.element.id;

        if (caller) {
            const cd = calculatorData.sampleStandard as any;
            const currentValue = cd[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                let editedSampleStandard = { ...sampleStandard };

                editedSampleStandard[caller] = e.value;

                setCalculatorData({ ...calculatorData, sampleStandard: editedSampleStandard });

                let fieldUpdate: FieldUpdate = {
                    field: caller,
                    value: e.value,
                    rowId: calculatorId,
                    routeId: 1
                };

                console.log("ABOUT TO UPDATE SAMPLE ");

                await updateQnmrCalculatorStandard(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating qNMR Field");
                    });
            }
        }
    }

    function calculateClimateAirDensity(): number {
        if (calculatorData && calculatorData.calculator) {
            let temperature = calculatorData.calculator.temperature ? calculatorData.calculator.temperature : 0;
            let airPressure = calculatorData.calculator.airPressure ? calculatorData.calculator.airPressure : 0;
            let relativeAirHumidity = calculatorData.calculator.relativeHumidity ? calculatorData.calculator.relativeHumidity : 0;

            //todo need to set these constants somewhere
            return PWMath.roundNumber((0.34848 * airPressure - 0.009024 * relativeAirHumidity * Math.exp(0.0612 * temperature)) / (273.15 + temperature), 4);
        }
    }

    function onGetBenchSheetDataButtonClicked() {
        if (calculatorId && benchSheetId) {


        }
    }

    async function getQnmrCalculatorData() {
        if (calculatorId) {
            await CrudFunctions.getData(`calculator/qnmr/${calculatorId}`)
                .then((data) => {
                    console.log("QNMR Data");
                    console.log(data);

                    setCalculatorData(data);

                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Running qNMR Calculation");
                });
        }
    }

    async function getBenchSheetData() {
        await CrudFunctions.getData(`calculator/qnmrsampledata/${benchSheetId}`)
            .then((data) => {


            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet Data");
            });
    }

    async function updateQnmrCalculatorField(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("calculator/qnmrcalculatorfield", fieldUpdate)
    }

    async function updateQnmrCalculatorStandard(fieldUpdate: FieldUpdate) {
        return await CrudFunctions.putData("calculator/qnmrcalculatorstandardfield", fieldUpdate)
    }

    async function updateQnmrMeasurement(measurement: QnmrMeasurement) {
        return await CrudFunctions.putData("calculator/qnmrmeasurement", measurement)
    }

    async function getAllData() {
        await Promise.all([
            getBalances(),
            getQnmrCalculatorData()
        ]).then(() => {
            calculateClimateAirDensity();
            setDataLoaded(true)
        });
    }


    /*
    //temp
    function createData() {
                let calcData: QnmrCalculatorData = {
                header: { },
            measurements: [
            {qnmrMeasurementId: '1', sampleIdx: 1, outlier: false },
            {qnmrMeasurementId: '2', sampleIdx: 2, outlier: false },
            {qnmrMeasurementId: '3', sampleIdx: 3, outlier: false },
            {qnmrMeasurementId: '4', sampleIdx: 4, outlier: false },
            ],
            uncertaintyEvaluation: [
            {qnmrMeasurementUncertaintEvaluationId: '1', position: 1, label: 'P(Reference)[%]' },
            {qnmrMeasurementUncertaintEvaluationId: '2', position: 2, label: 'm(Reference)[mg]' },
            {qnmrMeasurementUncertaintEvaluationId: '3', position: 3, label: 'm(Sample)[mg]' },
            {qnmrMeasurementUncertaintEvaluationId: '4', position: 4, label: 'M(Reference)[g/mol]' },
            {qnmrMeasurementUncertaintEvaluationId: '5', position: 5, label: 'M(Sample)[g/mol]' },
            {qnmrMeasurementUncertaintEvaluationId: '6', position: 6, label: 'Integration' },
            {qnmrMeasurementUncertaintEvaluationId: '7', position: 7, label: 'Rep' }
            ]
        }

            setCalculatorData(calcData);
    }
            */

    function refChemicalFormulaTextSymbolButton(e: any) {
        setStandardEdit("ref");
        showSymbolEditor("molecularFormulaString",
            calculatorData.refStandard && calculatorData.refStandard.molecularFormulaString ?
                calculatorData.refStandard.molecularFormulaString : undefined);
    }

    function sampleChemicalFormulaTextSymbolButton(e: any) {
        setStandardEdit("molecularFormulaString");
        showSymbolEditor("molecularFormulaString",
            calculatorData.sampleStandard && calculatorData.sampleStandard.molecularFormulaString ?
                calculatorData.sampleStandard.molecularFormulaString : undefined);
    }

    function showSymbolEditor(propName: string, text?: string) {
        let symbolEditorParams: SymbolEditorProps = {
            inputText: text,
            propName: propName,
            editorVisible: true
        };

        setSymbolEditorProps(symbolEditorParams);
    }

    function hideSymbolEditor() {
        let symbolEditorParams = { ...symbolEditorProps };
        symbolEditorParams.editorVisible = false;

        setSymbolEditorProps(symbolEditorParams);
    }

    function showRefMolecularFormulaParserDialog() {
        setStandardEdit("ref");
        setRefMolecularFormulaParserDialogVisible(true);
    }

    function showSampleMolecularFormulaParserDialog() {
        setStandardEdit("sample");
        setSampleMolecularFormulaParserDialogVisible(true);
    }

    function hideRefMolecularFormulaParserDialog() {
        setStandardEdit("");
        setRefMolecularFormulaParserDialogVisible(false);
    }

    function hideSampleMolecularFormulaParserDialog() {
        setStandardEdit("");
        setSampleMolecularFormulaParserDialogVisible(false);
    }

    async function getBalances() {
        await CrudFunctions.getData(`common/assetsbytechnologyandassettype/${BenchSheetTechnologyType.QNuclearMagneticResonance}/${AssetType.Balance}`)
            .then(data => {
                setBalances(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            });
    }



    //parseformula/{formula}



    function getValidationDateColor() {
        if (!selectedBalance || !selectedBalance.revalidationDate)
            return;

        const d = selectedBalance.revalidationDate;

        let now = new Date();
        let date = new Date(d);

        if (date.getTime() < now.getTime()) {
            return "date-expired";
        }

        const inOneMonth = new Date(now.setMonth(now.getMonth() + 1));

        if (date.getTime() < inOneMonth.getTime())
            return "date-warning";
        else
            return "date-good";
    }



    function criticalValuesForGrubbsTestRender(e: any) {
        //console.log("criticalValuesForGrubbsTestRender");
        //console.log(e);

        return <div className="pw-bold">{e.data.a005}</div>;
    }

    function contributionBarChartValueAxisRender(e: any) {
        return <div>Contribution (%)</div>;
    }

    function formatGrubbsTestMeasurement(e: any) {

        console.log(formatGrubbsTestMeasurement);
        console.log(e);

        if (calculatorData && calculatorData.grubbsTestData &&
            calculatorData.grubbsTestData.measurements) {

            if (e.data) {
                var min = Math.min(...calculatorData.grubbsTestData.measurements.map(m => m.value));

                if (min === e.data.value)
                    return <div className="pw-red pw-bg-rr-200">{e.text}</div>

                var max = Math.max(...calculatorData.grubbsTestData.measurements.map(m => m.value));

                if (max === e.data.value)
                    return <div className="pw-green pw-bg-vg-200">{e.text}</div>

                return <div>{e.text}</div>;
            }
        }
        else
            return <div>{e.text}</div>;
    }

    //navigation

    function goBack() {
        history.back();
    }


    function onShowMoreClick() {
        setShowMore(!showMore);
    }

    function onPanelButtonClick(e: any) {
        resetAllButtons();

        let id: string = e.target.id;

        if (e.target.tagName === "SPAN") {
            id = e.target.parentElement.id;

            const button = e.target.parentElement as HTMLDivElement;

            if (button)
                button.className = "tab-button-active";
        }
        else {
            const button = e.target as HTMLDivElement;

            if (button)
                button.className = "tab-button-active";
        }

        hideAllComponents();

        const panel = document.getElementById(id.replace("Button", "")) as HTMLDivElement;
        if (panel)
            panel.style.display = "";
    }

    function resetAllButtons() {

        const summaryPanelButton = document.getElementById("summaryPanelButton") as HTMLDivElement;
        summaryPanelButton.className = "tab-button-inactive";

        const basicPanelButton = document.getElementById("basicPanelButton") as HTMLDivElement;
        basicPanelButton.className = "tab-button-inactive";

        /*
        const balancePanelButton = document.getElementById("balancePanelButton") as HTMLDivElement;
        balancePanelButton.className = "tab-button-inactive";
        */

        const measurementPanelButton = document.getElementById("measurementPanelButton") as HTMLDivElement;
        measurementPanelButton.className = "tab-button-inactive";

        const evaluationPanelButton = document.getElementById("evaluationPanelButton") as HTMLDivElement;
        evaluationPanelButton.className = "tab-button-inactive";

        const lcMsIsotopicPurityPanelButton = document.getElementById("lcMsIsotopicPurityPanelButton") as HTMLDivElement;
        lcMsIsotopicPurityPanelButton.className = "tab-button-inactive";

        const anovaPanelButton = document.getElementById("anovaPanelButton") as HTMLDivElement;
        anovaPanelButton.className = "tab-button-inactive";

        const outlierTestPanelButton = document.getElementById("outlierTestPanelButton") as HTMLDivElement;
        outlierTestPanelButton.className = "tab-button-inactive";

        const massUncertaintyPanelButton = document.getElementById("massUncertaintyPanelButton") as HTMLDivElement;
        massUncertaintyPanelButton.className = "tab-button-inactive";

        const chemicalReferencesPanelButton = document.getElementById("chemicalReferencesPanelButton") as HTMLDivElement;
        chemicalReferencesPanelButton.className = "tab-button-inactive";
    }

    function hideAllComponents(onload: boolean = false) {
        if (!onload) {
            const summaryPanel = document.getElementById("summaryPanel") as HTMLDivElement;
            if (summaryPanel)
                summaryPanel.style.display = "none";
        }

        const basicPanel = document.getElementById("basicPanel") as HTMLDivElement;
        if (basicPanel)
            basicPanel.style.display = "none";

        const measurementPanel = document.getElementById("measurementPanel") as HTMLDivElement;
        if (measurementPanel)
            measurementPanel.style.display = "none";

        const evaluationPanel = document.getElementById("evaluationPanel") as HTMLDivElement;
        if (evaluationPanel)
            evaluationPanel.style.display = "none";

        const lcMsIsotopicPurityPanel = document.getElementById("lcMsIsotopicPurityPanel") as HTMLDivElement;
        if (lcMsIsotopicPurityPanel)
            lcMsIsotopicPurityPanel.style.display = "none";

        const anovaPanel = document.getElementById("anovaPanel") as HTMLDivElement;
        if (anovaPanel)
            anovaPanel.style.display = "none";

        const outlierTestPanel = document.getElementById("outlierTestPanel") as HTMLDivElement;
        if (outlierTestPanel)
            outlierTestPanel.style.display = "none";

        const massUncertaintyPanel = document.getElementById("massUncertaintyPanel") as HTMLDivElement;
        if (massUncertaintyPanel)
            massUncertaintyPanel.style.display = "none";

        const chemicalReferencesPanel = document.getElementById("chemicalReferencesPanel") as HTMLDivElement;
        if (chemicalReferencesPanel)
            chemicalReferencesPanel.style.display = "none";

    }

    function initializeUI() {
        hideAllComponents(true);

        const summaryPanelButton = document.getElementById("summaryPanelButton") as HTMLButtonElement;
        summaryPanelButton.click();
    }


    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
